import type { TaskId } from './types.tsx';

const completeJwmTaskName = (taskId: TaskId) => `jwm-onboarding-checklist.${taskId}`;
const completeJswTaskName = (taskId: TaskId) => `jsw-onboarding-checklist.${taskId}`;
const completeJsmTaskName = (taskId: TaskId) => `jsm-onboarding-checklist.${taskId}`;
const completePlanTaskName = (taskId: TaskId) => `plan-onboarding-checklist.${taskId}`;

export const PACKAGE_NAME = 'jiraSoftwareOnboardingQuickstart';
export const PACKAGE_TEAM_NAME = 'jlove-makkuro';

export const DEFAULT_NUDGE_WIDTH = 320;

export const ROOT_TASK = 'root';
export const REVERSE_TRIAL_ROOT_TASK = 'reverseTrialRoot';

// Objects Stored in Backend & Local
export const QUICKSTART_TYPE = {
	JSM_BUSINESS_QUICKSTART_KEY: 'jsm-business-checklist',
	OLD_GLOBAL_QUICKSTART_KEY: 'jsw-onboarding-checklist',
} as const;
export type QUICKSTART_TYPE = (typeof QUICKSTART_TYPE)[keyof typeof QUICKSTART_TYPE];
// Generates backend property key for quickstart visibility
export const getBackendVisibilityKey = (type: QUICKSTART_TYPE) => `${type}.visibility`;

// Old backend property keys for quickstart visibility shared by all of Quickstart
export const KEY_OPEN = 'jsw-onboarding-checklist.open';
export const KEY_DISMISSED = 'jsw-onboarding-checklist.dismissed';

export const KEY_TASK_GROUP = 'jsw-onboarding-checklist.task-group';
export const KEY_OPEN_TASK = 'jsw-onboarding-checklist.open-task';
export const KEY_HAS_EVER_SEEN_ROUTE = 'jsw-onboarding-checklist.has-ever-seen-route';
export const KEY_PREMIUM_TRIAL_REOPENED = 'jsw-onboarding-checklist.reopened-on-premium-trial';

// Used for analytics event context for primary (drawer) view
export const ACTION_SUBJECT_ID = 'nuxOnboardingQuickstart';
export const SOURCE = 'nuxOnboardingQuickstart';

/**
 * JSW onboarding for setting user properties
 * Reference:
 * src/packages/software/onboarding/src/view/view.js
 */
export const KEY_JSW_ONBOARDING = 'jsw.onboarding';
export const convertToHaveSeenItTask = (taskId: TaskId) => `${taskId}.have-seenit`;

// @todo: Clean up and remove any reference to this const when productionising simplify_kanban_qs
export const IDENTIFY_SMALL_CHUNKS_OF_WORK_KANBAN_TASK = completeJswTaskName(
	'identify-small-chunks-of-work-kanban',
);
export const PREMIUM_REVERSE_TRIAL_TASK = completeJswTaskName('premium-reverse-trial-task');
export const ADVANCED_ROADMAPS_TASK = completeJswTaskName('advanced-roadmaps-task');
export const SITE_ADMINISTRATIONS_TASK = completeJswTaskName('site-administrations-task');

// Plan tasks
export const PLAN_CREATE_MORE_WORK_ITEMS_TASK = completePlanTaskName('create-work-items-task');
export const PLAN_BRING_IN_THE_TEAM_TASK = completePlanTaskName('bring-team-task');
export const PLAN_CONFIRM_SCOPE_AND_SETTINGS_TASK = completePlanTaskName('confirm-scope-task');
export const PLAN_CONNECT_RELATED_WORK_TASK = completePlanTaskName('connect-related-work-task');
export const PLAN_ADJUST_DETAILS_TASK = completePlanTaskName('adjust-details-task');
export const PLAN_SHARE_YOUR_PLAN_WITH_THE_WORLD_TASK =
	completePlanTaskName('share-your-plan-task');

// JWM tasks
export const JWM_PROJECT_SUMMARY_TASK = completeJwmTaskName('jwm-project-summary-task');
export const JWM_CREATE_SAMPLE_PROJECT_TASK = completeJwmTaskName('jwm-create-sample-project-task');
export const JWM_BACKGROUND_COLOR_TASK = completeJwmTaskName('background-color-task');
export const JWM_CALENDAR_TASK = completeJwmTaskName('calendar-task');
export const JWM_TIMELINE_TASK = completeJwmTaskName('timeline-task');
export const JWM_BUILD_FORM_TASK = completeJwmTaskName('build-form-task');
export const JWM_FEEDBACK_TASK = completeJwmTaskName('feedback-task');
export const JWM_FEEDBACK_SAMPLE_PROJECT_TASK = completeJwmTaskName('feedback-sample-project-task');
export const JWM_CREATE_YOUR_OWN_PROJECT_TASK = completeJwmTaskName('create-your-own-project-task');
export const JWM_CREATE_PROJECT_TASK = completeJwmTaskName('jwm-create-project-task');
export const JWM_SHARE_PROJECT_TASK = completeJwmTaskName('share-project-task');
export const JWM_TRELLO_CROSSFLOW_TASK_IMPORT = completeJwmTaskName('trello-crossflow-import');
export const JWM_SUMMARY_AND_REPORTS_TASK = completeJwmTaskName('summary-and-reports-task');
export const JWM_TRELLO_CROSSFLOW_CALENDAR_AND_TIMELINE_TASK = completeJwmTaskName(
	'trello-crossflow-calendar-and-timeline',
);
export const JWM_TRELLO_CROSSFLOW_TASK_KEY_DIFFERENCES = completeJwmTaskName(
	'trello-crossflow-key-differences',
);

// definition comes from https://hello.atlassian.net/wiki/spaces/Spork/pages/3962753569/Tailored+Onboarding+-+Operations
export const OPS_JTBD_1_LIST = completeJwmTaskName('OPS_JTBD_1_LIST');
export const OPS_JTBD_2_BOARD = completeJwmTaskName('OPS_JTBD_2_BOARD');
export const OPS_JTBD_2_FORMS = completeJwmTaskName('OPS_JTBD_2_FORMS');
export const OPS_JTBD_2_SUMMARY = completeJwmTaskName('OPS_JTBD_2_SUMMARY');
export const OPS_JTBD_3_LIST = completeJwmTaskName('OPS_JTBD_3_LIST');
export const OPS_JTBD_3_CALENDAR = completeJwmTaskName('OPS_JTBD_3_CALENDAR');
export const OPS_JTBD_3_PAGES = completeJwmTaskName('OPS_JTBD_3_PAGES');
export const OPS_JTBD_4_LIST = completeJwmTaskName('OPS_JTBD_4_LIST');
export const OPS_JTBD_4_CALENDAR = completeJwmTaskName('OPS_JTBD_4_CALENDAR');
export const OPS_JTBD_4_TIMELINE = completeJwmTaskName('OPS_JTBD_4_TIMELINE');
export const OPS_JTBD_4_SUMMARY = completeJwmTaskName('OPS_JTBD_4_SUMMARY');
export const OPS_JTBD_5_LIST = completeJwmTaskName('OPS_JTBD_5_LIST');
export const OPS_JTBD_5_TIMELINE = completeJwmTaskName('OPS_JTBD_5_TIMELINE');
export const OPS_JTBD_5_PAGES = completeJwmTaskName('OPS_JTBD_5_PAGES');
export const OPS_JTBD_6_PAGES = completeJwmTaskName('OPS_JTBD_6_PAGES');
export const OPS_JTBD_7_LIST = completeJwmTaskName('OPS_JTBD_7_LIST');
export const OPS_JTBD_7_CALENDAR = completeJwmTaskName('OPS_JTBD_7_CALENDAR');
export const OPS_JTBD_0_LIST = completeJwmTaskName('OPS_JTBD_0_LIST');
export const OPS_JTBD_0_TIMELINE = completeJwmTaskName('OPS_JTBD_0_TIMELINE');
export const OPS_JTBD_0_PAGES = completeJwmTaskName('OPS_JTBD_0_PAGES');
export const OPS_JTBD_DEFAULT_LIST = completeJwmTaskName('OPS_JTBD_DEFAULT_LIST');
export const OPS_JTBD_DEFAULT_BOARD = completeJwmTaskName('OPS_JTBD_DEFAULT_BOARD');
export const OPS_JTBD_DEFAULT_INVITES = completeJwmTaskName('OPS_JTBD_DEFAULT_INVITES');

// definition comes from https://hello.atlassian.net/wiki/spaces/Spork/pages/3820424905/Tailored+Onboarding+-+Personalised+Landing+based+on+JTBD#Dynamic-Personalisation
export const MKT_JTBD_1_LIST = completeJwmTaskName('MKT_JTBD_1_LIST');
export const MKT_JTBD_1_CALENDAR = completeJwmTaskName('MKT_JTBD_1_CALENDAR');
export const MKT_JTBD_1_TIMELINE = completeJwmTaskName('MKT_JTBD_1_TIMELINE');
export const MKT_JTBD_2_CALENDAR = completeJwmTaskName('MKT_JTBD_2_CALENDAR');
export const MKT_JTBD_2_PAGES = completeJwmTaskName('MKT_JTBD_2_PAGES');
export const MKT_JTBD_3_PAGES = completeJwmTaskName('MKT_JTBD_3_PAGES');
export const MKT_JTBD_3_TIMELINE = completeJwmTaskName('MKT_JTBD_3_TIMELINE');
export const MKT_JTBD_4_LIST = completeJwmTaskName('MKT_JTBD_4_LIST');
export const MKT_JTBD_4_SUMMARY = completeJwmTaskName('MKT_JTBD_4_SUMMARY');
export const MKT_JTBD_4_PAGES = completeJwmTaskName('MKT_JTBD_4_PAGES');
export const MKT_JTBD_5_TIMELINE = completeJwmTaskName('MKT_JTBD_5_TIMELINE');
export const MKT_JTBD_5_SUMMARY = completeJwmTaskName('MKT_JTBD_5_SUMMARY');
export const MKT_JTBD_6_FORMS = completeJwmTaskName('MKT_JTBD_6_FORMS');
export const MKT_JTBD_0_LIST = completeJwmTaskName('MKT_JTBD_0_LIST');
export const MKT_JTBD_0_CALENDAR = completeJwmTaskName('MKT_JTBD_0_CALENDAR');
export const MKT_JTBD_0_TIMELINE = completeJwmTaskName('MKT_JTBD_0_TIMELINE');
export const MKT_JTBD_0_SUMMARY = completeJwmTaskName('MKT_JTBD_0_SUMMARY');
export const MKT_JTBD_DEFAULT_LIST = completeJwmTaskName('MKT_JTBD_DEFAULT_LIST');
export const MKT_JTBD_DEFAULT_BOARD = completeJwmTaskName('MKT_JTBD_DEFAULT_BOARD');
export const MKT_JTBD_DEFAULT_PROJECT = completeJwmTaskName('MKT_JTBD_DEFAULT_PROJECT');
export const MKT_JTBD_DEFAULT_INVITES = completeJwmTaskName('MKT_JTBD_DEFAULT_INVITES');

// tailored onboarding M3: Kanban tasks for non-software team type
export const TAILORED_ONBOARDING_SUMMARY = completeJswTaskName('TAILORED_ONBOARDING_SUMMARY');
export const TAILORED_ONBOARDING_LIST = completeJswTaskName('TAILORED_ONBOARDING_LIST');
export const TAILORED_ONBOARDING_CALENDAR = completeJswTaskName('TAILORED_ONBOARDING_CALENDAR');
export const TAILORED_ONBOARDING_BOARD = completeJswTaskName('TAILORED_ONBOARDING_BOARD');
export const TAILORED_ONBOARDING_TIMELINE = completeJswTaskName('TAILORED_ONBOARDING_TIMELINE');
export const TAILORED_ONBOARDING_PAGES = completeJswTaskName('TAILORED_ONBOARDING_PAGES');
export const TAILORED_ONBOARDING_FORM = completeJswTaskName('TAILORED_ONBOARDING_FORM');
export const TAILORED_ONBOARDING_INVITE = completeJswTaskName('TAILORED_ONBOARDING_INVITE');
export const TAILORED_ONBOARDING_BACKLOG = completeJswTaskName('TAILORED_ONBOARDING_BACKLOG');
export const TAILORED_ONBOARDING_CODE = completeJswTaskName('TAILORED_ONBOARDING_CODE');

// JSM tasks
export const JSM_CUSTOMIZE_YOUR_REQUEST_TYPES_TASK = completeJsmTaskName(
	'customize-your-request-types-task',
);
export const JSM_REVIEW_YOUR_CHANNELS_TASK = completeJsmTaskName('review-your-channels-task');
export const JSM_INVITE_YOUR_TEAM_TASK = completeJsmTaskName('invite-your-team-task');

export const KEY_HAS_NAVIGATED_FROM_LINK = 'jsw-onboarding-checklist.has.navigated.from.link';
export const ACCOUNT_ID = 'accountId';

export const QUICKSTART_UI_STATUS = {
	NOT_LOADED: 'NOT_LOADED',
	OPEN: 'OPEN',
	MINIMIZED: 'MINIMIZED',
	DISMISSED: 'DISMISSED',
} as const;

export const BACKLOG_CREATE_SPRINT_NUDGE_ID = 'backlogCreateSprintNudge';

export const PROJECT_SETTING_PAGES: string[] = [
	'legacy-project-settings-people',
	'project-settings-software-access',
];

export const JSM_BUSINESS_TASKS = [
	JSM_REVIEW_YOUR_CHANNELS_TASK,
	JSM_CUSTOMIZE_YOUR_REQUEST_TYPES_TASK,
	JSM_INVITE_YOUR_TEAM_TASK,
];

// Right now we list all the available tasks manually.

export const allTasksExcludingJSMBusinessTasks = [
	IDENTIFY_SMALL_CHUNKS_OF_WORK_KANBAN_TASK,
	PREMIUM_REVERSE_TRIAL_TASK,
	ADVANCED_ROADMAPS_TASK,
	SITE_ADMINISTRATIONS_TASK,
	JWM_CREATE_PROJECT_TASK,
	JWM_CREATE_SAMPLE_PROJECT_TASK,
	JWM_PROJECT_SUMMARY_TASK,
	JWM_BACKGROUND_COLOR_TASK,
	JWM_CALENDAR_TASK,
	JWM_TIMELINE_TASK,
	JWM_BUILD_FORM_TASK,
	JWM_FEEDBACK_TASK,
	JWM_SHARE_PROJECT_TASK,
	JWM_CREATE_YOUR_OWN_PROJECT_TASK,
	JWM_TRELLO_CROSSFLOW_TASK_IMPORT,
	JWM_SUMMARY_AND_REPORTS_TASK,
	JWM_TRELLO_CROSSFLOW_CALENDAR_AND_TIMELINE_TASK,
	JWM_TRELLO_CROSSFLOW_TASK_KEY_DIFFERENCES,
	JWM_FEEDBACK_SAMPLE_PROJECT_TASK,
	OPS_JTBD_1_LIST,
	OPS_JTBD_2_BOARD,
	OPS_JTBD_2_FORMS,
	OPS_JTBD_2_SUMMARY,
	OPS_JTBD_3_LIST,
	OPS_JTBD_3_CALENDAR,
	OPS_JTBD_3_PAGES,
	OPS_JTBD_4_LIST,
	OPS_JTBD_4_CALENDAR,
	OPS_JTBD_4_TIMELINE,
	OPS_JTBD_4_SUMMARY,
	OPS_JTBD_5_LIST,
	OPS_JTBD_5_TIMELINE,
	OPS_JTBD_5_PAGES,
	OPS_JTBD_6_PAGES,
	OPS_JTBD_7_LIST,
	OPS_JTBD_7_CALENDAR,
	OPS_JTBD_0_LIST,
	OPS_JTBD_0_TIMELINE,
	OPS_JTBD_0_PAGES,
	OPS_JTBD_DEFAULT_LIST,
	OPS_JTBD_DEFAULT_BOARD,
	OPS_JTBD_DEFAULT_INVITES,
	MKT_JTBD_1_LIST,
	MKT_JTBD_1_CALENDAR,
	MKT_JTBD_1_TIMELINE,
	MKT_JTBD_2_CALENDAR,
	MKT_JTBD_2_PAGES,
	MKT_JTBD_3_PAGES,
	MKT_JTBD_3_TIMELINE,
	MKT_JTBD_4_LIST,
	MKT_JTBD_4_SUMMARY,
	MKT_JTBD_4_PAGES,
	MKT_JTBD_5_TIMELINE,
	MKT_JTBD_5_SUMMARY,
	MKT_JTBD_6_FORMS,
	MKT_JTBD_0_LIST,
	MKT_JTBD_0_CALENDAR,
	MKT_JTBD_0_TIMELINE,
	MKT_JTBD_0_SUMMARY,
	MKT_JTBD_DEFAULT_LIST,
	MKT_JTBD_DEFAULT_BOARD,
	MKT_JTBD_DEFAULT_PROJECT,
	MKT_JTBD_DEFAULT_INVITES,
	// Plan tasks
	PLAN_CREATE_MORE_WORK_ITEMS_TASK,
	PLAN_BRING_IN_THE_TEAM_TASK,
	PLAN_CONFIRM_SCOPE_AND_SETTINGS_TASK,
	PLAN_CONNECT_RELATED_WORK_TASK,
	PLAN_ADJUST_DETAILS_TASK,
	PLAN_SHARE_YOUR_PLAN_WITH_THE_WORLD_TASK,
	// Tailored onboarding M3
	TAILORED_ONBOARDING_SUMMARY,
	TAILORED_ONBOARDING_LIST,
	TAILORED_ONBOARDING_CALENDAR,
	TAILORED_ONBOARDING_BOARD,
	TAILORED_ONBOARDING_TIMELINE,
	TAILORED_ONBOARDING_PAGES,
	TAILORED_ONBOARDING_FORM,
	TAILORED_ONBOARDING_INVITE,
	TAILORED_ONBOARDING_BACKLOG,
	TAILORED_ONBOARDING_CODE,
	//
];

export const SAMPLE_DATA_PROJECT_KEYS_TO_HIDE_ON = ['SAM1'];
